import { faChevronLeft, faChevronRight, faRotateLeft, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';
import React from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { fetchImageBlobUrl, saveImageRotation } from 'utils/rotateImage';
import { toastConfig } from 'utils/toastConfig';
import './ImageModal.css';

export interface ImageModalHandle {
  show: () => void;
  hide: () => void;
}

export interface ImageModalProps {
  image: string | undefined;
  size: 'sm' | 'lg' | 'xl' | undefined;
  googleFileId?: string;
  caption?: string;
  onRotated?: (googleFileId: string) => void | Promise<void>;
  onPrevious?: () => void;
  onNext?: () => void;
  imageIndex?: number;
  imageCount?: number;
}

type SlideDirection = 'next' | 'prev';

const isGoingNext = (previousIndex: number, nextIndex: number, count: number) => {
  if (count <= 1 || previousIndex === nextIndex) return true;
  if ((previousIndex + 1) % count === nextIndex) return true;
  if ((previousIndex - 1 + count) % count === nextIndex) return false;
  return nextIndex > previousIndex;
};

const snapshotImage = (image: HTMLImageElement | null) => {
  if (!image || !image.complete || image.naturalWidth === 0) return undefined;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext('2d');
    if (!context) return undefined;
    context.drawImage(image, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.8);
  } catch {
    return image.currentSrc || image.src;
  }
};

const ImageModal = React.forwardRef<ImageModalHandle, ImageModalProps>(
  (
    {
      image,
      size,
      googleFileId,
      caption,
      onRotated,
      onPrevious,
      onNext,
      imageIndex,
      imageCount
    },
    ref
  ) => {
    const [show, setShow] = React.useState(false);
    const [rotationDegrees, setRotationDegrees] = React.useState(0);
    const [saving, setSaving] = React.useState(false);
    const [displayImage, setDisplayImage] = React.useState(image);
    const [activeGoogleFileId, setActiveGoogleFileId] = React.useState(googleFileId);
    const [outgoing, setOutgoing] = React.useState<{
      src: string;
      rotation: number;
    } | null>(null);
    const [slideDirection, setSlideDirection] = React.useState<SlideDirection | null>(
      null
    );
    const imageRef = React.useRef<HTMLImageElement | null>(null);
    const displayedIndexRef = React.useRef<number | undefined>(undefined);
    const rotationRef = React.useRef(0);
    rotationRef.current = rotationDegrees;

    const resetSlide = () => {
      setOutgoing(null);
      setSlideDirection(null);
      displayedIndexRef.current = undefined;
    };

    React.useImperativeHandle(ref, () => ({
      show: () => setShow(true),
      hide: () => {
        setShow(false);
        setRotationDegrees(0);
        resetSlide();
      }
    }));

    React.useEffect(() => {
      if (!show || imageIndex == null || !image) {
        setDisplayImage(image);
        setActiveGoogleFileId(googleFileId);
        return;
      }

      const previousIndex = displayedIndexRef.current;
      if (previousIndex == null) {
        displayedIndexRef.current = imageIndex;
        setDisplayImage(image);
        setActiveGoogleFileId(googleFileId);
        setRotationDegrees(0);
        return;
      }

      if (previousIndex === imageIndex) {
        setDisplayImage(image);
        setActiveGoogleFileId(googleFileId);
        return;
      }

      const snapshot = snapshotImage(imageRef.current);
      if (snapshot) {
        setOutgoing({ src: snapshot, rotation: rotationRef.current });
      }
      setSlideDirection(
        isGoingNext(previousIndex, imageIndex, imageCount ?? 0) ? 'next' : 'prev'
      );
      displayedIndexRef.current = imageIndex;
      setDisplayImage(image);
      setActiveGoogleFileId(googleFileId);
      setRotationDegrees(0);
    }, [image, googleFileId, imageIndex, imageCount, show]);

    React.useEffect(() => {
      if (!slideDirection) return;
      const timeout = window.setTimeout(() => {
        setOutgoing(null);
        setSlideDirection(null);
      }, 320);
      return () => window.clearTimeout(timeout);
    }, [slideDirection, displayImage]);

    const canSaveRotation = Boolean(activeGoogleFileId);
    const hasPendingRotation = rotationDegrees !== 0;
    const canNavigate = Boolean(onPrevious && onNext && (imageCount ?? 0) > 1);
    const isSliding = Boolean(slideDirection);

    React.useEffect(() => {
      if (!show || !canNavigate || isSliding) return;

      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          onPrevious?.();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          onNext?.();
        }
      };

      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [show, canNavigate, isSliding, onPrevious, onNext]);

    const handleClose = () => {
      setShow(false);
      setRotationDegrees(0);
      resetSlide();
    };

    const rotateBy = (delta: number) => {
      setRotationDegrees(prev => (((prev + delta) % 360) + 360) % 360);
    };

    const handleSaveRotation = async () => {
      if (!canSaveRotation || !hasPendingRotation || !activeGoogleFileId) {
        return;
      }

      setSaving(true);
      try {
        await saveImageRotation(activeGoogleFileId, rotationDegrees);
        const nextUrl = await fetchImageBlobUrl(activeGoogleFileId);

        if (displayImage?.startsWith('blob:')) {
          URL.revokeObjectURL(displayImage);
        }

        setDisplayImage(nextUrl);
        setRotationDegrees(0);
        toast.success('Image rotated and saved', { ...toastConfig, autoClose: 2000 });
        await onRotated?.(activeGoogleFileId);
      } catch (error) {
        const message =
          error instanceof AxiosError
            ? error.response?.data?.errorMessage || 'Failed to save rotated image'
            : 'Failed to save rotated image';
        toast.error(message, { ...toastConfig, autoClose: 3000 });
      } finally {
        setSaving(false);
      }
    };

    return (
      <Modal size={size} centered show={show} onHide={handleClose}>
        <Modal.Body>
          <div className="image-modal-viewport">
            {canNavigate && (
              <Button
                variant="light"
                className="position-absolute start-0 ms-2"
                style={{ zIndex: 2 }}
                onClick={onPrevious}
                disabled={isSliding}
                aria-label="Previous photo"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>
            )}
            {outgoing && slideDirection && (
              <div
                className={`image-modal-slide image-modal-slide--outgoing image-modal-slide--outgoing-${slideDirection}`}
              >
                <img
                  src={outgoing.src}
                  alt=""
                  style={{ transform: `rotate(${outgoing.rotation}deg)` }}
                />
              </div>
            )}
            <div
              className={`image-modal-slide${
                slideDirection
                  ? ` image-modal-slide--incoming-${slideDirection}`
                  : ''
              }`}
            >
              <img
                ref={imageRef}
                src={displayImage}
                alt={caption || 'Preview'}
                style={{
                  transform: `rotate(${rotationDegrees}deg)`,
                  transition: isSliding ? undefined : 'transform 0.2s ease'
                }}
              />
            </div>
            {canNavigate && (
              <Button
                variant="light"
                className="position-absolute end-0 me-2"
                style={{ zIndex: 2 }}
                onClick={onNext}
                disabled={isSliding}
                aria-label="Next photo"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            )}
          </div>
          {caption && (
            <div className="text-center text-body-tertiary fs-9 mt-2">{caption}</div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between flex-wrap gap-2">
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => rotateBy(-90)}
              disabled={saving || isSliding}
              title="Rotate left"
            >
              <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
              Rotate left
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => rotateBy(90)}
              disabled={saving || isSliding}
              title="Rotate right"
            >
              <FontAwesomeIcon icon={faRotateRight} className="me-2" />
              Rotate right
            </Button>
            {canSaveRotation && (
              <Button
                variant="primary"
                onClick={handleSaveRotation}
                disabled={saving || !hasPendingRotation || isSliding}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Saving…
                  </>
                ) : (
                  'Save rotation'
                )}
              </Button>
            )}
          </div>
          <div className="d-flex align-items-center gap-3">
            {canNavigate && imageCount != null && imageIndex != null && (
              <span className="text-body-tertiary fs-9">
                {imageIndex + 1} / {imageCount}
              </span>
            )}
            <Button variant="danger" onClick={handleClose} disabled={saving}>
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
);

export default ImageModal;
